import { ArrowLeft, Mic, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type InsightSpan,
  type Meeting,
  type MeetingProposal,
  type RecordingInsights,
  type SourceMaterial,
  type Transcript,
} from "@/lib/api";
import { DeleteSourceControl } from "@/components/DeleteSourceControl";
import { cn } from "@/lib/cn";
import { formatCost, formatDate, formatDuration, formatTime } from "@/lib/format";
import { useAudioFragment } from "@/lib/useAudioFragment";
import { usePipelineRun } from "@/lib/usePipelineRun";
import { DebugTab } from "@/pages/DebugTab";
import { PipelinePanel } from "@/pages/PipelinePanel";
import { StatusPill } from "@/pages/Recordings";
import { MeetingHeading, RecordingHeading } from "@/pages/recording/Headings";
import { InsightsTab, EntitiesTab } from "@/pages/recording/InsightTabs";
import { TranscriptTab } from "@/pages/recording/TranscriptTab";
import { CompletePanel, RecordingWizard } from "@/pages/recording/Wizard";

const TABS = ["transcript", "insights", "entities", "debug"] as const;

type Tab = (typeof TABS)[number];

const TAB_LABEL: Record<Tab, string> = {
  transcript: "Transcript",
  insights: "Insights",
  entities: "Entities",
  debug: "Debug",
};

/**
 * UI-5 — one recording or meeting, drawn one of two ways depending which it is
 * (recording/meeting split, M5.1 follow-up).
 *
 * A recording's audio phase (transcribe…name_speakers) hands the owner two things to do
 * before there is anything to read: confirm who was speaking, and decide how the
 * timeline divides into meetings. Those are steps toward a destination, not independent
 * views of the same finished thing, so a recording gets `RecordingWizard` rather than a
 * tab bar — SpeakersTab and MeetingsPhase are its two steps.
 *
 * A meeting is what the content phase (correct_transcript, summarize, extract_insights,
 * tag_entities, relations) actually produces something to look at *from*, so it keeps
 * the tab bar: Transcript, Insights and Entities are three honest readings of one
 * finished thing, not a sequence. Before that run, a meeting shows only its transcript —
 * with review mode and "Split here", since that is when checking and dividing it pays
 * off — and "Split here" is gone once the run is done. The transcript is a *reading* of the meeting —
 * turns, timecodes, who said what. The word-by-word playable view is Transcript Review
 * (UI-12), a full-window screen of its own; the card at the top of the Transcript tab is
 * its front door.
 *
 * Debug (ARCH-8) is absent rather than disabled when developer mode is off, on both
 * paths: a recording gets it as a collapsed section under the wizard, a meeting keeps it
 * as a tab — either way it is a control for one person on one machine, and a greyed-out
 * tab would be a permanent question about what is behind it.
 */
export function RecordingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, recordings, settings, entities, refreshRecordings, refreshSetup, refreshEntities, refreshReview } =
    useApp();

  const [source, setSource] = useState<SourceMaterial | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [insights, setInsights] = useState<RecordingInsights | null>(null);
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [proposal, setProposal] = useState<MeetingProposal | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("transcript");

  // UI-7 — when an address carries `?insight=<id>` (followed from the review queue, or from
  // an insight card's "View in source"), the Transcript tab highlights the spans that insight
  // was drawn from. The param drives it, so the same jump works from anywhere.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightInsight = searchParams.get("insight");
  // SRCH-4 — a quote found by "Find in transcripts" jumps to its line the same way.
  const highlightSegment = searchParams.get("segment");
  const [highlightSpans, setHighlightSpans] = useState<InsightSpan[] | null>(null);

  const pipeline = usePipelineRun(api, id);
  const player = useAudioFragment(api, id);

  // The org's people, for the confirmation typeahead (SPCH-6). Held once by the shell
  // (UI-6), so a name typed here and a person added in the manager are the same list.
  const persons = useMemo(() => entities.filter((entity) => entity.type === "person"), [entities]);

  // SPCH-6 — confirm who a voice is, then swap the row for what the Server returns and
  // refresh the people list, since a free-text name has just created a person.
  const confirmSpeaker = async (speakerId: string, who: { personId: string } | { name: string }) => {
    if (!id) return;

    const updated = await api.confirmSpeaker(id, speakerId, who);

    setTranscript((current) =>
      current
        ? { ...current, speakers: current.speakers.map((s) => (s.id === speakerId ? updated : s)) }
        : current,
    );

    refreshEntities();
  };

  // The row from the list, for the two numbers the source itself does not carry — how
  // long it is and how many people are on it. Both come from the poll the shell already
  // runs, so the header does not wait on a request of its own.
  const row = recordings.find((recording) => recording.id === id);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    setSource(null);
    setTranscript(null);
    setInsights(null);
    setMeetings(null);
    setProposal(null);
    setProblem(null);

    void Promise.all([api.getSource(id), api.getTranscript(id)])
      .then(([loadedSource, loadedTranscript]) => {
        if (cancelled) return;
        setSource(loadedSource);
        setTranscript(loadedTranscript);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "There is no recording at this address."
            : "That recording could not be loaded.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  // A run finishing while this page is open has just produced the transcript (and the
  // insights) the tabs are about to show — re-read them, or the tabs appear over the
  // stale answers fetched while the run was still going.
  const runStatus = pipeline.run?.status;
  const previousRunStatus = useRef<string | undefined>(undefined);

  useEffect(() => {
    const was = previousRunStatus.current;
    previousRunStatus.current = runStatus;

    if (!id || runStatus !== "done" || (was !== "processing" && was !== "queued")) return;
    let cancelled = false;

    setInsights(null); // The lazy tab effect re-reads on the next open.
    setProposal(null); // A run that just finished has just made a new one.

    void Promise.all([api.getSource(id), api.getTranscript(id)])
      .then(([loadedSource, loadedTranscript]) => {
        if (cancelled) return;
        setSource(loadedSource);
        setTranscript(loadedTranscript);
      })
      .catch(() => {
        // The stale read stays on screen; the next visit re-reads.
      });

    return () => {
      cancelled = true;
    };
  }, [api, id, runStatus]);

  // The Insights and Entities tabs read one payload, fetched the first time either is
  // opened rather than with the transcript: most visits to a recording are to read it, and
  // an insight it produced dozens of is a second query the Transcript tab does not need.
  useEffect(() => {
    if (!id || insights !== null || (tab !== "insights" && tab !== "entities")) return;
    let cancelled = false;

    void api
      .getRecordingInsights(id)
      .then((loaded) => {
        if (!cancelled) setInsights(loaded);
      })
      .catch(() => {
        // A recording with no insights answers with empty lists, not an error; a genuine
        // failure leaves the tab's own empty state, which is the honest fallback.
        if (!cancelled) setInsights({ insights: [], entities: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [api, id, tab, insights]);

  // The recording's meetings (recording/meeting split), read as soon as the audio phase
  // is done: the wizard that replaces a recording's tabs shows its second step
  // immediately, not behind a tab click. Gated on the run rather
  // than just the kind, so a recording still mid-audio-phase (whose default meeting does
  // not exist yet) does not fetch a list that is guaranteed empty.
  useEffect(() => {
    if (!id || source?.kind !== "recording" || runStatus !== "done" || meetings !== null) return;
    let cancelled = false;

    void api
      .listMeetings(id)
      .then((loaded) => {
        if (!cancelled) setMeetings(loaded);
      })
      .catch(() => {
        if (!cancelled) setMeetings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id, source?.kind, runStatus, meetings]);

  // SPCH-13 — the meetings the audio phase proposes, read with the meetings list and for the
  // same reason: step 2 of the wizard shows them at once. A Server or a recording older than
  // the proposal answers `unavailable`, and so does a failed read, which then shows nothing.
  useEffect(() => {
    if (!id || source?.kind !== "recording" || runStatus !== "done" || proposal !== null) return;
    let cancelled = false;

    void api
      .getMeetingProposal(id)
      .then((loaded) => {
        if (!cancelled) setProposal(loaded);
      })
      .catch(() => {
        if (!cancelled) setProposal({ status: "unavailable", canApply: false, stretches: [], model: null });
      });

    return () => {
      cancelled = true;
    };
  }, [api, id, source?.kind, runStatus, proposal]);

  // Live while any meeting's own content-phase run is still going — the same cadence and
  // the same reason `usePipelineRun` polls the recording's own run: this is the "next
  // phase" the wizard promises is visible, so a meeting that finishes while this page is
  // open should update here without a manual refresh.
  const meetingsInFlight =
    meetings?.some((meeting) => meeting.status === "queued" || meeting.status === "processing") ?? false;

  useEffect(() => {
    if (!id || !meetingsInFlight) return;

    const timer = setInterval(() => {
      void api
        .listMeetings(id)
        .then(setMeetings)
        .catch(() => {
          // A transient failure leaves the last-known list on screen; the next tick retries.
        });
    }, 2000);

    return () => clearInterval(timer);
  }, [api, id, meetingsInFlight]);

  // UI-7 — resolve the insight named in the address to its spans, and land on the transcript
  // where they are. A cleared param (the reader navigating away from the jump) clears the
  // highlight rather than leaving a stale one lit.
  useEffect(() => {
    if (!highlightInsight) {
      setHighlightSpans(
        highlightSegment && id
          ? [{ sourceMaterialId: id, sourceSegmentId: highlightSegment, sequence: 0, startOffset: null, endOffset: null }]
          : null,
      );
      if (highlightSegment) setTab("transcript");
      return;
    }

    let cancelled = false;
    setTab("transcript");

    void api
      .getInsightProvenance(highlightInsight)
      .then((spans) => {
        if (!cancelled) setHighlightSpans(spans);
      })
      .catch(() => {
        // A stale or foreign id highlights nothing rather than failing the page.
        if (!cancelled) setHighlightSpans(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api, highlightInsight, highlightSegment, id]);

  if (problem) {
    return (
      <div className="px-[var(--space-8)] py-[var(--space-12)] text-center">
        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">{problem}</p>
        <Link
          to="/recordings"
          className="mt-[var(--space-4)] inline-block text-[length:var(--text-sm)] text-[var(--text-secondary)] underline"
        >
          Back to recordings
        </Link>
      </div>
    );
  }

  if (!source) {
    return (
      <p className="px-[var(--space-8)] py-[var(--space-8)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  // From `source` rather than `row` (CAP-11): a rename updates `source` at once, and
  // waiting on the next recordings poll to agree would make the save look like it did
  // nothing.
  const title =
    source.title ?? source.originalFilename ?? (source.kind === "meeting" ? "Untitled meeting" : "Untitled recording");
  const run = pipeline.run;
  const finished = run?.status === "done";

  // A recording never reaches this tab bar at all (see the wizard branch below) — these
  // four are what a meeting (or any other kind the content phase runs over) shows once
  // its own run is done. Debug is absent rather than disabled when developer mode is off
  // (ARCH-8), the same rule the wizard's own Debug section follows.
  const tabs = TABS.filter((current) => current !== "debug" || settings?.developerMode);
  const activeTab = tabs.includes(tab) ? tab : tabs[0];

  // The run's total Anthropic spend, or null when nothing in it called the cloud
  // Reasoner at all — an all-local/GPU run has no cost pill to show, rather than a "$0.0000"
  // that would claim a call that never happened.
  const costs = run?.steps.map((step) => step.costUsd).filter((cost) => cost !== null) ?? [];
  const totalCostUsd = costs.length > 0 ? costs.reduce((sum, cost) => sum + cost, 0) : null;

  const isMeeting = source.kind === "meeting";

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <Link
        to={isMeeting && source.parentSourceMaterialId ? `/recordings/${source.parentSourceMaterialId}` : "/recordings"}
        className="inline-flex w-fit items-center gap-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        {isMeeting ? "Back to recording" : "Recordings"}
      </Link>

      <header className="flex flex-col gap-[var(--space-2)]">
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          {isMeeting ? (
            <MeetingHeading
              meeting={source}
              onSaved={(updated) =>
                setSource((current) =>
                  current
                    ? { ...current, title: updated.title, description: updated.description }
                    : current,
                )
              }
            />
          ) : (
            <RecordingHeading
              title={title}
              recordingId={source.id}
              onSaved={(updatedTitle) => {
                setSource((current) => (current ? { ...current, title: updatedTitle } : current));
                // The header text above actually reads from the `recordings` list (`row`)
                // once it has loaded, so an edit only shows up here once that list agrees.
                refreshRecordings();
              }}
            />
          )}

          <DeleteSourceControl sourceId={source.id} />
        </div>

        <div className="flex flex-wrap items-center gap-[var(--space-3)]">
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {[formatDate(source.sourceDate ?? source.createdAt), formatTime(source.startedAt), formatDuration(row?.durationMs)]
              .filter(Boolean)
              .join(" · ")}
          </p>

          {totalCostUsd !== null && (
            <span className="rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-secondary)]">
              {formatCost(totalCostUsd)}
            </span>
          )}

          {!isMeeting && (source.recorderName ?? source.originalFilename) && (
            <span className="inline-flex items-center gap-1 rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-secondary)]">
              {source.recorderName ? (
                <Mic size={11} strokeWidth={2} />
              ) : (
                <Upload size={11} strokeWidth={2} />
              )}
              {source.recorderName ?? source.originalFilename}
            </span>
          )}

          {source.language && (
            <span className="rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-secondary)]">
              {source.language}
            </span>
          )}

          <StatusPill status={row?.status ?? run?.status ?? null} />
        </div>

        {isMeeting && source.summary && (
          <p className="max-w-[70ch] text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">
            {source.summary}
          </p>
        )}
      </header>

      {/* One or the other, never both. A run still going is the whole story of the
          screen; a finished one is a line, and everything below it is the point. */}
      {finished ? (
        <CompletePanel
          run={run}
          sourceId={source.id}
          state={pipeline}
          elapsedMs={
            run.completedAt ? Date.parse(run.completedAt) - Date.parse(run.createdAt) : null
          }
          worker={lastWorker(run.steps)}
          onChanged={() => {
            refreshRecordings();
            refreshSetup();
          }}
        />
      ) : (
        <PipelinePanel sourceId={source.id} kind={source.kind} state={pipeline} />
      )}

      {/* A meeting before its content phase: the transcript the audio phase produced is
          already there, and this is when reviewing it and splitting it are worth doing —
          before correct_transcript and extraction read it. Not while a run is going,
          though: a split would move segments out from under the steps reading them. */}
      {isMeeting && !finished && run !== undefined && (
        <TranscriptTab
          transcript={transcript}
          recordingId={source.id}
          highlight={highlightSpans}
          splittable={run === null || run.status === "failed" || run.status === "cancelled"}
        />
      )}

      {finished && source.kind === "recording" && (
        <RecordingWizard
          transcript={transcript}
          player={player}
          persons={persons}
          onConfirmSpeaker={confirmSpeaker}
          meetings={meetings}
          onMeetingsChanged={(updated) =>
            setMeetings((current) => (current ? current.map((m) => (m.id === updated.id ? updated : m)) : current))
          }
          proposal={proposal}
          onProposalApplied={(adopted) => {
            setMeetings(adopted);
            // Adopted: nothing started yet, so it can be adopted again, and nothing waits on it.
            setProposal((current) =>
              current ? { ...current, canApply: true, canReplace: false, awaitingAdoption: false } : current,
            );
          }}
          run={run}
          recordingId={source.id}
          startedAt={source.startedAt}
          developerMode={settings?.developerMode ?? false}
        />
      )}

      {finished && source.kind !== "recording" && (
        <>
          <nav
            aria-label="Recording sections"
            className="flex gap-1 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-1 backdrop-blur-[var(--blur-sm)]"
          >
            {tabs.map((current) => (
              <button
                key={current}
                type="button"
                role="tab"
                aria-selected={activeTab === current}
                onClick={() => setTab(current)}
                className={cn(
                  "rounded-[var(--radius-md)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-medium transition-colors",
                  activeTab === current
                    ? "bg-[var(--surface-2)] text-[var(--text-primary)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                )}
              >
                {TAB_LABEL[current]}
              </button>
            ))}
          </nav>

          {activeTab === "transcript" && (
            <TranscriptTab
              transcript={transcript}
              recordingId={source.id}
              highlight={highlightSpans}
            />
          )}
          {activeTab === "insights" && (
            <InsightsTab
              sourceId={source.id}
              sourceName={title}
              insights={insights}
              // INS-7 — re-read the tab either way: approved rows change state, and a refused
              // approval means the list on screen is not what is waiting.
              onApproved={() => {
                setInsights(null);
                refreshReview();
              }}
              // Re-read in place rather than through the loading state, so the list — and the
              // sentence saying why it changed — stays on screen.
              onStale={() => {
                void api
                  .getRecordingInsights(source.id)
                  .then(setInsights)
                  .catch(() => undefined);
              }}
              onViewInSource={(insightId) => {
                setSearchParams(
                  (params) => {
                    params.set("insight", insightId);
                    return params;
                  },
                  { replace: true },
                );
                setTab("transcript");
              }}
            />
          )}
          {activeTab === "entities" && <EntitiesTab insights={insights} />}
          {activeTab === "debug" && settings?.developerMode && <DebugTab run={run} recordingId={source.id} />}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ the pipeline line

/**
 * The machine that ran the last step that named one.
 *
 * The last rather than the first, because a run requeued onto a second worker finished
 * somewhere other than where it started, and where it finished is what the line is
 * reporting.
 */
function lastWorker(steps: { claimedByName: string | null }[]): string | null {
  for (let i = steps.length - 1; i >= 0; i--) {
    const name = steps[i]?.claimedByName;
    if (name) return name;
  }

  return null;
}
